import { useContext, useState } from 'react';
import { toast } from 'sonner';
import { GoogleSheetsIcon } from '@/components/ui/icons/google-product-icons';
import { AgentCard } from '@/components/agent-chat/agent-card.component';
import {
  CardFooter,
  CardMeta,
  CardStatus,
} from '@/components/agent-chat/card-parts.component';
import { CopilotAttachContext } from '@/components/agent-chat/copilot-attach.context';
import {
  googlePickError,
  useGooglePicker,
} from '@/hooks/use-google-picker.hook';
import { useLazyGetWorkspaceSheetCsvQuery } from '@/store/features/workspace-connections.feature';
import { apiErrorCode } from '@/lib/appsumo-upgrade';

const SHEETS_LOGO = (
  <div className='flex size-[20px] flex-none items-center justify-center rounded-[6px] border border-border bg-background'>
    <GoogleSheetsIcon className='size-[12px]' />
  </div>
);

/**
 * Clu's answer to a pasted Google Sheets link (display `google_sheet_import`,
 * CLE-515). Cleanlist only has drive.file access, so it can read a linked
 * sheet only after the member opens that one file in Google's Picker with
 * their own connected Google. Import tries a plain read first (sheets Cleanlist made
 * or that were picked before), opens the Picker on just that file when Google
 * won't share it yet, then hands the linked tab to the chat's usual import
 * mapping card, exactly like an attached CSV.
 */
export function GoogleSheetImportCard({
  result,
}: {
  result: Record<string, unknown>;
}) {
  const attach = useContext(CopilotAttachContext);
  const { canPick, organizationId, pick } = useGooglePicker();
  const [readCsv] = useLazyGetWorkspaceSheetCsvQuery();
  const [imported, setImported] = useState(false);

  const spreadsheetId = String(result.spreadsheet_id ?? '');
  const gid = typeof result.gid === 'number' ? result.gid : null;
  // Each member imports through their own Google (Clu only shows the card
  // once they've connected it; it can be disconnected since).
  const canImport = canPick && !!attach;

  const read = () =>
    readCsv({
      provider: 'google_sheets',
      organizationId,
      spreadsheetId,
      gid,
    }).unwrap();

  /** False unlocks the button: nothing was handed to the import. */
  const handleImport = async () => {
    if (!attach) return false;
    try {
      let tab;
      try {
        tab = await read();
      } catch (err) {
        if (apiErrorCode(err) !== 'sheet_unreachable') throw err;
        // Google won't share it yet: the user grants access to this one file.
        const picked = await pick(spreadsheetId);
        if (!picked) return false;
        tab = await read();
      }
      const name =
        [tab.spreadsheetName, tab.sheetName].filter(Boolean).join(' - ') ||
        'Google Sheet';
      attach([new File([tab.csv], `${name}.csv`, { type: 'text/csv' })]);
      setImported(true);
      return true;
    } catch (err) {
      toast.error(googlePickError(err));
      return false;
    }
  };

  if (imported)
    return (
      <AgentCard
        leading={SHEETS_LOGO}
        title='Google Sheet'
        trailing={<CardStatus>Mapping below</CardStatus>}
      />
    );

  const fact = canImport
    ? 'Opens with Google'
    : canPick
      ? 'Import from the chat'
      : 'Google Sheets not connected';
  return (
    <AgentCard leading={SHEETS_LOGO} title='Google Sheet'>
      {canImport ? (
        <CardFooter
          control={<CardMeta className='min-w-0'>{fact}</CardMeta>}
          primary={{
            label: 'Import',
            workingLabel: 'Importing',
            onClick: handleImport,
          }}
        />
      ) : (
        <CardMeta>{fact}</CardMeta>
      )}
    </AgentCard>
  );
}
